import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import { generarDataUrlQR } from "./qrUtils";
import { mostrarCargandoPDF } from "./pdfLoadingUtils";
import { mostrarToast } from "./toastUtils";

import CajaService from "../services/CajaService";
import SolicitudesService from "../services/SolicitudesService";
import SolicitudesEstudiosService from "../services/SolicitudesEstudiosService";
import ConveniosService from "../services/ConveniosService";
import PacientesService from "../services/PacientesService";
import DoctoresService from "../services/DoctoresService";
import ParametrosEstudioService from "../services/ParametrosEstudioService";
import ResultadosService from "../services/ResultadosService";
import MuestrasService from "../services/MuestrasService";
import EstudiosService from "../services/EstudiosService";
import EstudiosIntService from "../services/EstudiosIntService";
import HistorialEstudiosService from "../services/HistorialEstudiosService";
import HistorialService from "../services/HistorialService";

import headerInformeImg from "../img/header-informeResultados.png";
import logoMembreteImg from "../img/logo-membrete.png";
import pieDePaginaImg from "../img/pieDePaginaResultados.jpg";
import firmaRicardoImg from "../img/firma_ricardo.jpg";

const imageCache = {};
const getCachedImage = (url) => {
  if (!url) return Promise.resolve("");
  if (imageCache[url]) return Promise.resolve(imageCache[url]);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL(url.endsWith(".png") ? "image/png" : "image/jpeg");
        imageCache[url] = dataURL;
        resolve(dataURL);
      } catch (e) {
        imageCache[url] = url;
        resolve(url);
      }
    };
    img.onerror = () => {
      imageCache[url] = url;
      resolve(url);
    };
    img.src = url;
  });
};

const qrCache = {};

const normStr = (s) => String(s || "").trim().toUpperCase();

const isSubEstudioFlag = (est) => {
  if (!est) return false;
  return (
    est.registroExamenSub === true ||
    est.registroExamenSub === 1 ||
    String(est.registroExamenSub).toLowerCase() === "si" ||
    String(est.registroExamenSub).toLowerCase() === "s"
  );
};

const parseEdadString = (edadStr) => {
  if (!edadStr) return [0, "AÑOS"];
  const str = String(edadStr).trim();
  const match = str.match(/(\d+(?:\.\d+)?)\s*(AÑOS?|MESES?|DIAS?|DÍAS?|ANOS?)?/i);
  if (match && match[1]) {
    const unidad = match[2] ? match[2].toUpperCase() : "AÑOS";
    return [parseFloat(match[1]), unidad];
  }
  const val = parseFloat(str);
  return [isNaN(val) ? 0 : val, "AÑOS"];
};

const edadEnDias = (valor, unidad) => {
  const v = parseFloat(valor) || 0;
  const u = String(unidad || "AÑOS").toUpperCase();
  if (u.includes("MES")) return v * 30.4375;
  if (u.includes("AÑO") || u.includes("ANO")) return v * 365.25;
  return v;
};

const coincideGenero = (invGenero, pacGenero) => {
  const ig = String(invGenero || "").trim().toLowerCase();
  const pg = String(pacGenero || "").trim().toUpperCase().startsWith("F") ? "F" : "M";
  if (!ig || ig === "ambos" || ig === "ambas" || ig === "todos" || ig === "todas" || ig === "general") return true;
  if (pg === "F") {
    return ig === "mujer" || ig === "mujeres" || ig === "femenino" || ig === "f";
  } else {
    return ig === "hombre" || ig === "hombres" || ig === "masculino" || ig === "m";
  }
};

const obtenerIntervaloCoincidente = (p, pac) => {
  if (!pac) return null;
  const pacGenero = String(pac.sexo || "").trim().toUpperCase().startsWith("F") ? "F" : "M";
  const [edadVal, edadUni] = parseEdadString(pac.edad);
  const pacDias = edadEnDias(edadVal, edadUni);

  const intervals = extractIntervalos(p);
  if (!Array.isArray(intervals) || intervals.length === 0) return null;

  return intervals.find(inv => {
    const gen = inv.genero ?? inv.sexo;
    if (!coincideGenero(gen, pacGenero)) return false;

    const tipoEdad = inv.tipoEdad ?? inv.tipo_edad ?? inv.unidadEdad ?? inv.unidad_edad ?? "AÑOS";
    const rawMin = inv.edadMin ?? inv.edad_min ?? inv.minimoEdad ?? inv.edadMinima;
    const rawMax = inv.edadMax ?? inv.edad_max ?? inv.maximoEdad ?? inv.edadMaxima;

    const minDias = rawMin !== undefined && rawMin !== null && String(rawMin).trim() !== ""
      ? edadEnDias(rawMin, tipoEdad)
      : -Infinity;
    const maxDias = rawMax !== undefined && rawMax !== null && String(rawMax).trim() !== ""
      ? edadEnDias(rawMax, tipoEdad)
      : Infinity;

    return pacDias >= minDias && pacDias <= maxDias;
  }) || null;
};

const htmlToPlainText = (raw) => {
  if (!raw) return "";
  let str = String(raw);
  str = str
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/h[1-6]>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<\/?ul[^>]*>/gi, "")
    .replace(/<\/?ol[^>]*>/gi, "")
    .replace(/<\/?(?:strong|b|em|i|u|s|span|sub|sup|a|font|mark|small|del|ins|abbr|cite|code|pre|blockquote|table|tr|td|th|thead|tbody|h[1-6]|div)[^>]*>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&deg;/gi, "°")
    .replace(/&plusmn;/gi, "±")
    .replace(/&micro;/gi, "µ")
    .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec));
  return str
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .join("\n");
};

/**
 * Analiza HTML enriquecido (de ReactQuill) y extrae bloques de párrafos/listas con formato
 * (negrita, cursiva, subrayado).
 */
const parseHtmlToStyledBlocks = (html) => {
  if (!html || typeof html !== "string") return [];
  const trimmed = html.trim();
  if (!trimmed || trimmed === "<p><br></p>" || trimmed === "<p></p>") return [];

  if (typeof window === "undefined" || !window.DOMParser) {
    const plain = htmlToPlainText(html);
    return plain.split("\n").filter(Boolean).map(line => [{ text: line, bold: false, italic: false, underline: false }]);
  }

  const parser = new DOMParser();
  const docHtml = parser.parseFromString(html, "text/html");
  const body = docHtml.body;
  if (!body) return [];

  const blocks = [];

  const extractInlineSpans = (node, styleState = { bold: false, italic: false, underline: false }, spans = []) => {
    if (!node) return spans;

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text) {
        spans.push({
          text,
          bold: Boolean(styleState.bold),
          italic: Boolean(styleState.italic),
          underline: Boolean(styleState.underline)
        });
      }
      return spans;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toUpperCase();

      if (tag === "BR") {
        spans.push({ text: "\n", isLineBreak: true });
        return spans;
      }

      const isBold = Boolean(styleState.bold || tag === "STRONG" || tag === "B" || (node.style && (node.style.fontWeight === "bold" || parseInt(node.style.fontWeight, 10) >= 600)));
      const isItalic = Boolean(styleState.italic || tag === "EM" || tag === "I" || (node.style && node.style.fontStyle === "italic"));
      const isUnderline = Boolean(styleState.underline || tag === "U" || (node.style && String(node.style.textDecoration).includes("underline")));

      const newStyle = {
        bold: isBold,
        italic: isItalic,
        underline: isUnderline
      };

      for (const child of node.childNodes) {
        extractInlineSpans(child, newStyle, spans);
      }
    }

    return spans;
  };

  function splitSpansByNewlines(spans) {
    const result = [];
    let current = [];
    for (const s of spans) {
      if (s.isLineBreak || s.text.includes("\n")) {
        const parts = s.text.split("\n");
        for (let i = 0; i < parts.length; i++) {
          if (parts[i]) {
            current.push({ ...s, text: parts[i], isLineBreak: false });
          }
          if (i < parts.length - 1) {
            if (current.length > 0) result.push(current);
            current = [];
          }
        }
      } else {
        current.push(s);
      }
    }
    if (current.length > 0) result.push(current);
    return result;
  }

  const processBlockNode = (node) => {
    if (!node) return;
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toUpperCase();

      if (tag === "UL") {
        for (const li of node.children) {
          if (li.tagName && li.tagName.toUpperCase() === "LI") {
            const spans = [{ text: "•  ", bold: true, italic: false, underline: false }];
            extractInlineSpans(li, { bold: false, italic: false, underline: false }, spans);
            splitSpansByNewlines(spans).forEach(b => { if (b.length > 0) blocks.push(b); });
          }
        }
        return;
      }

      if (tag === "OL") {
        let count = 1;
        for (const li of node.children) {
          if (li.tagName && li.tagName.toUpperCase() === "LI") {
            const spans = [{ text: `${count}.  `, bold: true, italic: false, underline: false }];
            extractInlineSpans(li, { bold: false, italic: false, underline: false }, spans);
            splitSpansByNewlines(spans).forEach(b => { if (b.length > 0) blocks.push(b); });
            count++;
          }
        }
        return;
      }

      if (["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6"].includes(tag)) {
        const spans = extractInlineSpans(node, { bold: false, italic: false, underline: false }, []);
        splitSpansByNewlines(spans).forEach(b => { if (b.length > 0) blocks.push(b); });
        return;
      }
    }

    const spans = extractInlineSpans(node, { bold: false, italic: false, underline: false }, []);
    splitSpansByNewlines(spans).forEach(b => { if (b.length > 0) blocks.push(b); });
  };

  for (const child of body.childNodes) {
    processBlockNode(child);
  }

  return blocks.filter(b => b.some(span => span.text && span.text.trim().length > 0));
};

const renderRichTextInPdf = (doc, blocks, startX, startY, maxWidth = 190, maxY = 185, topMargin = 20, fontSize = 9.0, lineHeight = 4.5) => {
  let finalY = startY;

  const getFontStyle = (bold, italic) => {
    if (bold && italic) return "bolditalic";
    if (bold) return "bold";
    if (italic) return "italic";
    return "normal";
  };

  for (let bIdx = 0; bIdx < blocks.length; bIdx++) {
    const spans = blocks[bIdx];

    const tokens = [];
    for (const span of spans) {
      const parts = span.text.split(/(\s+)/);
      for (const part of parts) {
        if (part.length > 0) {
          tokens.push({
            text: part,
            isSpace: /^\s+$/.test(part),
            bold: span.bold,
            italic: span.italic,
            underline: span.underline
          });
        }
      }
    }

    const lines = [];
    let currentLine = [];
    let currentLineWidth = 0;

    for (const token of tokens) {
      doc.setFont("helvetica", getFontStyle(token.bold, token.italic));
      doc.setFontSize(fontSize);
      const tokenW = doc.getTextWidth(token.text);

      if (currentLineWidth + tokenW <= maxWidth || currentLine.length === 0) {
        currentLine.push({ ...token, width: tokenW });
        currentLineWidth += tokenW;
      } else {
        if (currentLine.length > 0) {
          lines.push(currentLine);
        }
        if (token.isSpace) {
          currentLine = [];
          currentLineWidth = 0;
        } else {
          currentLine = [{ ...token, width: tokenW }];
          currentLineWidth = tokenW;
        }
      }
    }
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    for (const lineTokens of lines) {
      if (finalY > maxY) {
        doc.addPage();
        finalY = topMargin + 6;
      }

      let curX = startX;
      for (const t of lineTokens) {
        doc.setFont("helvetica", getFontStyle(t.bold, t.italic));
        doc.setFontSize(fontSize);
        doc.setTextColor(0, 0, 0);
        doc.text(t.text, curX, finalY);

        if (t.underline && !t.isSpace) {
          doc.saveGraphicsState();
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.2);
          doc.line(curX, finalY + 0.5, curX + t.width, finalY + 0.5);
          doc.restoreGraphicsState();
        }

        curX += t.width;
      }

      finalY += lineHeight;
    }

    if (bIdx < blocks.length - 1) {
      finalY += 1.0;
    }
  }

  return finalY;
};

const isMixtoParametro = (p) => {
  if (!p) return false;
  const tipoRef = String(p.tipoReferencia || p.tipoReferenciaParametro || p.tipoValor || "").toLowerCase().trim();
  if (tipoRef === "mixto" || tipoRef.includes("mixto")) return true;
  if (Array.isArray(p.mixto) && p.mixto.length > 0) return true;
  if (Array.isArray(p.valoresPosibles) && p.valoresPosibles.length > 0) return true;
  if (Array.isArray(p.valoresParametro) && p.valoresParametro.some(
    (item) => item && (item.esPredeterminado !== undefined || item.es_predeterminado !== undefined)
  )) return true;
  if (typeof p.valorReferencia === "string" && p.valorReferencia.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(p.valorReferencia);
      if (parsed && (parsed.valoresPosibles || parsed.valorPredeterminado !== undefined)) return true;
    } catch (e) { }
  }
  return false;
};

const getValorReferenciaMixto = (p) => {
  if (!p) return "";

  let valRef = "";
  let valPredet = "";

  // 1. Revisar si viene como JSON estructurado en valorReferencia
  const rawRef = p.valorReferencia || p.valor_referencia;
  if (typeof rawRef === "string" && rawRef.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(rawRef);
      if (parsed) {
        if (parsed.valorReferencia !== undefined && String(parsed.valorReferencia).trim() !== "") {
          valRef = String(parsed.valorReferencia).trim();
        }
        if (parsed.valorPredeterminado !== undefined && String(parsed.valorPredeterminado).trim() !== "") {
          valPredet = String(parsed.valorPredeterminado).trim();
        }
      }
    } catch (e) { }
  } else if (typeof rawRef === "string" && !rawRef.trim().startsWith("[")) {
    valRef = rawRef.trim();
  }

  // 2. Si no se obtuvo valorPredeterminado, revisar propiedades directas
  if (!valPredet) {
    if (p.valorPredeterminado !== undefined && p.valorPredeterminado !== null && String(p.valorPredeterminado).trim() !== "") {
      valPredet = String(p.valorPredeterminado).trim();
    } else if (p.valor_predeterminado !== undefined && p.valor_predeterminado !== null && String(p.valor_predeterminado).trim() !== "") {
      valPredet = String(p.valor_predeterminado).trim();
    }
  }

  // 3. Si aún no hay valorPredeterminado, buscar el elemento marcado con esPredeterminado: true en valoresParametro o mixto
  if (!valPredet) {
    const listSource = Array.isArray(p.valoresParametro)
      ? p.valoresParametro
      : Array.isArray(p.mixto)
      ? p.mixto
      : [];

    const predetItem = listSource.find(
      (item) => item && (item.esPredeterminado === true || item.esPredeterminado === "true" || item.esPredeterminado === 1)
    );

    if (predetItem && predetItem.valor !== undefined && predetItem.valor !== null && String(predetItem.valor).trim() !== "") {
      valPredet = String(predetItem.valor).trim();
    }
  }

  // 4. Prioridad: Valor de referencia primero
  const cleanRef = htmlToPlainText(valRef).trim();
  if (cleanRef) {
    return cleanRef;
  }

  // 5. En su defecto, Valor predeterminado
  const cleanPredet = htmlToPlainText(valPredet).trim();
  if (cleanPredet) {
    return cleanPredet;
  }

  // 6. Si no tiene ninguno de los dos, dejarlo vacío
  return "";
};

const extractIntervalos = (p) => {
  if (!p || isMixtoParametro(p)) return [];
  const parseIfArrayOrJSON = (val) => {
    let arr = [];
    if (Array.isArray(val)) arr = val;
    else if (typeof val === "string" && val.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) arr = parsed;
      } catch (e) {
        return [];
      }
    }
    if (arr.length > 0) {
      const esListaMixto = arr.some(
        (item) => item && (item.esPredeterminado !== undefined || item.es_predeterminado !== undefined)
      );
      if (esListaMixto) return [];
      return arr;
    }
    return [];
  };

  const sources = [
    p.intervalosBr,
    p.intervalosBR,
    p.intervalos,
    p.intervalos_br,
    p.valoresParametro,
    p.valoresReferencia,
    p.valorReferencia,
    p.valor_referencia,
    p.intervaloBiologicoRef
  ];

  for (const src of sources) {
    const res = parseIfArrayOrJSON(src);
    if (res.length > 0) return res;
  }

  return [];
};

const mesesEspanolCompletos = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

export const formatFechaHoraEspanol = (fechaInput) => {
  if (!fechaInput) return "-";
  const str = String(fechaInput).trim();
  if (!str || str === "-") return "-";

  if (mesesEspanolCompletos.some((m) => str.includes(m))) {
    return str;
  }

  let d = null;

  if (fechaInput instanceof Date) {
    d = fechaInput;
  } else {
    const ddmmyyyyMatch = str.match(
      /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/
    );
    if (ddmmyyyyMatch) {
      const dia = parseInt(ddmmyyyyMatch[1], 10);
      const mes = parseInt(ddmmyyyyMatch[2], 10) - 1;
      const anio = parseInt(ddmmyyyyMatch[3], 10);
      const hora = parseInt(ddmmyyyyMatch[4] || "0", 10);
      const min = parseInt(ddmmyyyyMatch[5] || "0", 10);
      d = new Date(anio, mes, dia, hora, min);
    } else {
      const isoMatch = str.match(
        /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})(?:[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/
      );
      if (isoMatch) {
        const anio = parseInt(isoMatch[1], 10);
        const mes = parseInt(isoMatch[2], 10) - 1;
        const dia = parseInt(isoMatch[3], 10);
        const hora = parseInt(isoMatch[4] || "0", 10);
        const min = parseInt(isoMatch[5] || "0", 10);
        d = new Date(anio, mes, dia, hora, min);
      } else {
        const parsed = new Date(str);
        if (!isNaN(parsed.getTime())) {
          d = parsed;
        }
      }
    }
  }

  if (!d || isNaN(d.getTime())) {
    return str;
  }

  const dia = String(d.getDate()).padStart(2, "0");
  const mes = mesesEspanolCompletos[d.getMonth()];
  const anio = d.getFullYear();
  const hrs = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");

  return `${dia} ${mes}, ${anio} ${hrs}:${mins} h`;
};

const drawRichTextRight = (doc, segments, rightX, y, fontSize = 7.5) => {
  doc.setFontSize(fontSize);
  let totalWidth = 0;
  segments.forEach((seg) => {
    doc.setFont("helvetica", seg.bold ? "bold" : "normal");
    totalWidth += doc.getTextWidth(seg.text);
  });
  let curX = rightX - totalWidth;
  segments.forEach((seg) => {
    doc.setFont("helvetica", seg.bold ? "bold" : "normal");
    doc.setTextColor(0, 0, 0);
    doc.text(seg.text, curX, y);
    curX += doc.getTextWidth(seg.text);
  });
};

const drawPatientBox = (doc, solicitudInfo, startY = 35, boxHeight = 16.5) => {
  doc.saveGraphicsState();

  // Borde doble: rectángulo exterior + dos rectángulos interiores
  doc.setDrawColor(206, 231, 247);
  doc.setLineWidth(0.4);

  // Rectángulo exterior
  doc.rect(10, startY, 190, boxHeight);

  // Rectángulo interior columna izquierda (114.2 mm de ancho, hasta 125.0 mm)
  doc.rect(10.8, startY + 0.8, 114.2, boxHeight - 1.6);

  // Rectángulo interior columna derecha (73.2 mm de ancho, desde 126.0 mm hasta 199.2 mm)
  doc.rect(126.0, startY + 0.8, 73.2, boxHeight - 1.6);

  const tieneEmpresa =
    solicitudInfo.nombreConvenio &&
    solicitudInfo.nombreConvenio !== "-" &&
    String(solicitudInfo.nombreConvenio).trim() !== "";

  const fontSize = 8.0;
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);

  let currY = startY + 4.2;

  if (tieneEmpresa) {
    doc.setFont("helvetica", "bold");
    doc.text("Nombre de empresa:", 12.5, currY);
    const wEmp = doc.getTextWidth("Nombre de empresa: ");
    doc.setFont("helvetica", "normal");
    doc.text(String(solicitudInfo.nombreConvenio).trim(), 12.5 + wEmp, currY);
    currY += 3.8;
  }

  // Columna Izquierda - Línea 1: Paciente y Edad
  doc.setFont("helvetica", "bold");
  doc.text("Paciente:", 12.5, currY);
  const wPac = doc.getTextWidth("Paciente: ");
  doc.setFont("helvetica", "normal");
  const nomPacStr = String(solicitudInfo.nombrePaciente || "-");

  const edadNacStr = `${solicitudInfo.edad || "-"} ${solicitudInfo.fechaNacimiento && solicitudInfo.fechaNacimiento !== "-"
      ? `(${solicitudInfo.fechaNacimiento})`
      : ""
    }`.trim();

  const wNomPac = doc.getTextWidth(nomPacStr);
  let edadX = Math.max(70, 12.5 + wPac + wNomPac + 2.5);
  if (edadX > 94) {
    edadX = 94;
    const truncatedNom = doc.splitTextToSize(nomPacStr, 94 - 12.5 - wPac - 2)[0];
    doc.text(truncatedNom, 12.5 + wPac, currY);
  } else {
    doc.text(nomPacStr, 12.5 + wPac, currY);
  }

  doc.setFont("helvetica", "bold");
  doc.text("Edad:", edadX, currY);
  const wEdad = doc.getTextWidth("Edad: ");
  doc.setFont("helvetica", "normal");
  doc.text(edadNacStr, edadX + wEdad, currY);

  // Columna Izquierda - Línea 2: Sexo
  currY += 3.8;
  doc.setFont("helvetica", "bold");
  doc.text("Sexo:", 12.5, currY);
  const wSexo = doc.getTextWidth("Sexo: ");
  doc.setFont("helvetica", "normal");
  doc.text(String(solicitudInfo.sexo || "-"), 12.5 + wSexo, currY);

  // Columna Izquierda - Línea 3: Dirigido
  currY += 3.8;
  doc.setFont("helvetica", "bold");
  doc.text("Dirigido:", 12.5, currY);
  const wDir = doc.getTextWidth("Dirigido: ");
  doc.setFont("helvetica", "normal");
  const docText = String(solicitudInfo.nombreDoctor || "DOCTOR").trim();
  const dirText = docText.toLowerCase().startsWith("dr") ? docText : `Dr(a) ${docText}`;
  doc.text(dirText, 12.5 + wDir, currY);

  // Columna Derecha: No. de Solicitud, Fecha de Solicitud, Fecha de Impresión
  // Espacio disponible: desde 127.5 mm hasta 199.0 mm (71.5 mm de ancho, garantiza que no se salga)
  const rightStartX = 127.5;
  let rightY = startY + 4.2;

  doc.setFont("helvetica", "bold");
  doc.text("No. de Solicitud:", rightStartX, rightY);
  const wNoSol = doc.getTextWidth("No. de Solicitud: ");
  doc.setFont("helvetica", "normal");
  doc.text(String(solicitudInfo.codigo || "-"), rightStartX + wNoSol, rightY);

  rightY += 3.8;
  doc.setFont("helvetica", "bold");
  doc.text("Fecha de Solicitud:", rightStartX, rightY);
  const wFSol = doc.getTextWidth("Fecha de Solicitud: ");
  doc.setFont("helvetica", "normal");
  doc.text(String(solicitudInfo.fechaSolicitudFormateada || "-"), rightStartX + wFSol, rightY);

  rightY += 3.8;
  doc.setFont("helvetica", "bold");
  doc.text("Fecha de Impresión:", rightStartX, rightY);
  const wFImp = doc.getTextWidth("Fecha de Impresión: ");
  doc.setFont("helvetica", "normal");
  doc.text(String(solicitudInfo.fechaImpresionFormateada || "-"), rightStartX + wFImp, rightY);

  doc.restoreGraphicsState();
};

const formatIntervaloBR = (intervalo, unidadMedida) => {
  if (intervalo === null || intervalo === undefined) return "";
  if (typeof intervalo === "string" || typeof intervalo === "number") {
    let s = String(intervalo).trim();
    if (unidadMedida && s.toLowerCase().endsWith(String(unidadMedida).toLowerCase().trim())) {
      s = s.slice(0, -String(unidadMedida).trim().length).trim();
    }
    return s;
  }

  const getVal = (...keys) => {
    for (const k of keys) {
      if (intervalo[k] !== undefined && intervalo[k] !== null && String(intervalo[k]).trim() !== "") {
        return intervalo[k];
      }
    }
    return "";
  };

  const minVal = getVal("intervaloMin", "intervalo_min", "minimo", "min", "valMin", "val_min");
  const maxVal = getVal("intervaloMax", "intervalo_max", "maximo", "max", "valMax", "val_max");

  let rangoStr = "";
  if (minVal !== "" && maxVal !== "") {
    rangoStr = `${minVal} a ${maxVal}`;
  } else if (minVal !== "") {
    rangoStr = `> ${minVal}`;
  } else if (maxVal !== "") {
    rangoStr = `< ${maxVal}`;
  }

  if (!rangoStr) {
    const rawVal = getVal("valor", "valorReferencia", "valor_referencia", "texto", "descripcion");
    if (rawVal) {
      let s = String(rawVal).trim();
      if (unidadMedida && s.toLowerCase().endsWith(String(unidadMedida).toLowerCase().trim())) {
        s = s.slice(0, -String(unidadMedida).trim().length).trim();
      }
      return s;
    }
  }

  return rangoStr;
};

const getCleanValorReferencia = (p) => {
  if (!p) return "";
  let val = p.valorReferencia || p.valor_referencia || p.valorPredeterminado || "";
  if (typeof val === "string" && val.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(val);
      if (parsed.valorReferencia) return parsed.valorReferencia;
      if (parsed.valorPredeterminado) return parsed.valorPredeterminado;
    } catch (e) { }
  }
  if (typeof val === "string" && val.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const lines = parsed.map((i) => formatIntervaloBR(i, p.unidadMedida)).filter(Boolean);
        if (lines.length > 0) return lines.join("\n");
      }
    } catch (e) { }
  }
  return val;
};

const formatParametroIntervalos = (p, pac = null) => {
  if (!p) return "";
  if (isMixtoParametro(p)) {
    return getValorReferenciaMixto(p);
  }
  const listIntervalos = extractIntervalos(p);

  if (Array.isArray(listIntervalos) && listIntervalos.length > 0) {
    if (pac) {
      const coincidente = obtenerIntervaloCoincidente(p, pac);
      if (coincidente) {
        return formatIntervaloBR(coincidente, p.unidadMedida);
      }
    }
    const lines = listIntervalos.map((i) => formatIntervaloBR(i, p.unidadMedida)).filter(Boolean);
    if (lines.length > 0) {
      return lines.join("\n");
    }
  }
  const cleanVal = getCleanValorReferencia(p);
  const rawText = cleanVal || p.valorReferencia || p.valor_referencia || p.intervaloBiologicoRef || p.valorPredeterminado || "";
  return htmlToPlainText(rawText);
};

const esParametroBR = (p) => {
  if (!p) return false;
  if (isMixtoParametro(p)) return false;
  const tipoRef = String(p.tipoReferencia || p.tipoReferenciaParametro || p.tipoValor || "").toLowerCase().trim();
  if (tipoRef.includes("criterio") || tipoRef.includes("mixto") || tipoRef.includes("texto")) {
    return false;
  }
  if (tipoRef === "intervalobr" || tipoRef === "intervalosbr" || tipoRef === "intervalo") {
    return true;
  }
  const intervalos = extractIntervalos(p);
  return Array.isArray(intervalos) && intervalos.length > 0;
};

export const generarPDFResultados = async (
  solicitudObj,
  studiesInput = [],
  isDownload = false,
  returnBlobOnly = false
) => {
  try {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const usuarioString = sessionStorage.getItem("usuario");
    const currentU = usuarioString ? JSON.parse(usuarioString) : {};

    const now = new Date();
    const fechaImpresionStr = formatFechaHoraEspanol(now);

    const idSol = solicitudObj.idSolicitud || solicitudObj.orden || solicitudObj.codigo;

    // Precarga inmediata y concurrente de imágenes y QR mientras se procesan los datos
    const imagesPromise = Promise.all([
      getCachedImage(headerInformeImg),
      getCachedImage(logoMembreteImg),
      getCachedImage(firmaRicardoImg),
      getCachedImage(pieDePaginaImg)
    ]);

    const qrPromise = (idSol && qrCache[idSol])
      ? Promise.resolve(qrCache[idSol])
      : idSol
        ? generarDataUrlQR(idSol).then(url => { qrCache[idSol] = url; return url; }).catch(() => null)
        : Promise.resolve(null);

    // Caches en memoria por ejecución para evitar llamadas HTTP duplicadas
    const estudioDetalleCache = new Map();
    const getEstudioDetalleConCache = async (id) => {
      if (!id) return null;
      const strId = String(id);
      if (estudioDetalleCache.has(strId)) return estudioDetalleCache.get(strId);
      const p = EstudiosService.getEstudioPorId(id).catch(() => null);
      estudioDetalleCache.set(strId, p);
      return p;
    };

    const muestrasCache = new Map();
    const getMuestrasConCache = async (id) => {
      if (!id) return [];
      const strId = String(id);
      if (muestrasCache.has(strId)) return muestrasCache.get(strId);
      const p = MuestrasService.getMuestrasPorEstudio(id).catch(() => []);
      muestrasCache.set(strId, p);
      return p;
    };

    const historialEstudioCache = new Map();
    const getHistorialEstudioConCache = async (idSolicitud, idCheck) => {
      const key = `${idSolicitud}_${idCheck}`;
      if (historialEstudioCache.has(key)) return historialEstudioCache.get(key);
      const p = HistorialEstudiosService.getHistorialPorEstudio(idSolicitud, idCheck).catch(() => null);
      historialEstudioCache.set(key, p);
      return p;
    };

    let esPagado = solicitudObj.pagado === true || solicitudObj.estatus === "P" || solicitudObj.estatusPago === "P";
    if (!esPagado && idSol) {
      try {
        const cajaRes = await CajaService.getCajaPorSolicitud(idSol);
        const payload = cajaRes?.data ?? cajaRes;
        const items = Array.isArray(payload?.caja) ? payload.caja : (Array.isArray(payload) ? payload : []);
        const datosCaja = items[0] ?? payload?.caja?.[0] ?? payload?.[0] ?? payload;
        const adeudo = Number(
          datosCaja?.adeudo ??
          datosCaja?.saldoPendiente ??
          datosCaja?.restante ??
          datosCaja?.saldo ??
          datosCaja?.pendiente ??
          1
        );
        if (adeudo <= 0) {
          esPagado = true;
        }
      } catch (e) { }
    }

    if (!esPagado) {
      const confirmResult = await Swal.fire({
        title: "Aviso de pago pendiente",
        text: returnBlobOnly
          ? "Esta solicitud aún no ha sido pagada. ¿Desea enviar el informe de resultados de todos modos?"
          : isDownload
            ? "Esta solicitud aún no ha sido pagada. ¿Desea descargar los resultados de todos modos?"
            : "Esta solicitud aún no ha sido pagada. ¿Desea imprimir los resultados de todos modos?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: returnBlobOnly
          ? "Sí, enviar"
          : isDownload
            ? "Sí, descargar"
            : "Sí, imprimir",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#18234f",
        cancelButtonColor: "#d33"
      });
      if (!confirmResult.isConfirmed) {
        return null;
      }
      await mostrarCargandoPDF(
        isDownload
          ? "Generando archivo PDF para descarga..."
          : returnBlobOnly
            ? "Preparando informe de resultados..."
            : "Generando informe de resultados completo...",
        "Procesando PDF"
      );
    }

    let solDetalle = solicitudObj;
    const yaTieneDatosCompletos =
      solicitudObj &&
      (solicitudObj.nombrePaciente || solicitudObj.paciente) &&
      (solicitudObj.codigo || solicitudObj.orden);

    if (!yaTieneDatosCompletos && idSol) {
      try {
        const resSingle = await SolicitudesService.getSolicitudPorId(idSol);
        const sFound = resSingle?.data?.solicitud || resSingle?.solicitud || resSingle?.data || resSingle;
        if (sFound) {
          solDetalle = { ...solicitudObj, ...sFound };
        }
      } catch (eSingle) {
        try {
          const resSolicitudes = await SolicitudesService.getSolicitudes();
          const listaSolicitudes = resSolicitudes?.data?.solicitudes || resSolicitudes?.data || [];
          const sFound = listaSolicitudes.find(s => String(s.idSolicitud || s.id || s.codigo || s.orden) === String(idSol));
          if (sFound) solDetalle = { ...solicitudObj, ...sFound };
        } catch (eAll) { }
      }
    }

    let nomConvenio = solDetalle.nombreConvenio || solDetalle.convenio || "";
    const idsDocs = solDetalle.idDoctores || (solDetalle.idDoctor ? [solDetalle.idDoctor] : []);

    const [
      resConvenio,
      resPaciente,
      nombresDoctores,
      resRes,
      resSolEst
    ] = await Promise.all([
      (!nomConvenio && solDetalle.idConvenio)
        ? ConveniosService.getConvenioPorId(solDetalle.idConvenio).catch(() => null)
        : Promise.resolve(null),
      (solDetalle.idPaciente)
        ? PacientesService.getPacientePorId(solDetalle.idPaciente).catch(() => null)
        : Promise.resolve(null),
      (idsDocs.length > 0)
        ? Promise.all(
          idsDocs.filter(Boolean).map(async (idDoc) => {
            try {
              const resDoctor = await DoctoresService.getDoctorPorId(idDoc);
              const doctor = resDoctor?.doctor?.[0] || resDoctor?.doctor || resDoctor?.data?.doctor?.[0] || resDoctor?.data?.doctor || resDoctor;
              return doctor ? `${doctor.nombre || ""} ${doctor.apPaterno || ""} ${doctor.apMaterno || ""}`.trim() : null;
            } catch (eD) {
              return null;
            }
          })
        ).catch(() => [])
        : Promise.resolve([]),
      ResultadosService.getResultadosPorSolicitud(idSol).catch(() => null),
      (!studiesInput || studiesInput.length === 0)
        ? SolicitudesEstudiosService.getEstudiosPorSolicitud(idSol).catch(() => null)
        : Promise.resolve(null)
    ]);

    if (resConvenio) {
      const convenio = resConvenio?.convenio?.[0] || resConvenio?.convenio || resConvenio;
      if (convenio) nomConvenio = convenio.nombre || nomConvenio;
    }

    let sexoPac = solDetalle.sexo || "-";
    let fechaNacPac = solDetalle.fechaNacimiento || "-";
    let edadPac = solDetalle.edad || "-";
    let nomPac = [solDetalle.nombrePaciente, solDetalle.apPaternoPaciente, solDetalle.apMaternoPaciente].filter(Boolean).join(" ")
      || [solDetalle.nombre, solDetalle.apPaterno, solDetalle.apMaterno].filter(Boolean).join(" ")
      || solDetalle.paciente
      || "PACIENTE";

    if (resPaciente) {
      const paciente = resPaciente?.paciente?.[0] || resPaciente?.paciente || resPaciente?.data?.paciente?.[0] || resPaciente?.data?.paciente || resPaciente;
      if (paciente) {
        const nomFromPac = `${paciente.nombre || ""} ${paciente.apPaterno || ""} ${paciente.apMaterno || ""}`.trim();
        if (nomFromPac) nomPac = nomFromPac;
        let sStr = paciente.sexo || sexoPac;
        if (sStr === "M") sStr = "Masculino";
        else if (sStr === "F") sStr = "Femenino";
        sexoPac = sStr;
        fechaNacPac = paciente.fechaNacimiento || fechaNacPac;
        edadPac = paciente.edad || edadPac;
      }
    }

    let docText = solDetalle.nombreDoctor || solDetalle.doctor || "";
    if (Array.isArray(nombresDoctores) && nombresDoctores.filter(Boolean).length > 0) {
      docText = nombresDoctores.filter(Boolean).join(" / ");
    }
    if (!docText) docText = "DOCTOR";

    const fechaSolicitudRaw = solDetalle.fechaSolicitud || solDetalle.fecha || now;
    const fechaSolicitudStr = formatFechaHoraEspanol(fechaSolicitudRaw);

    const solicitudInfo = {
      idSolicitud: idSol,
      codigo: solDetalle.codigo || solDetalle.orden || String(idSol),
      nombrePaciente: nomPac,
      nombreConvenio: nomConvenio,
      sexo: sexoPac,
      fechaNacimiento: fechaNacPac,
      edad: edadPac,
      nombreDoctor: docText,
      fechaSolicitud: fechaSolicitudRaw,
      fechaSolicitudFormateada: fechaSolicitudStr,
      fechaImpresionFormateada: fechaImpresionStr,
    };

    const nombrePacienteUpper = String(solicitudInfo.nombrePaciente || "PACIENTE").trim().replace(/\s+/g, " ").replace(/[\\/:*?"<>|]/g, "").toUpperCase();
    const codigoSolicitudClean = String(solicitudInfo.codigo || idSol || "").trim().replace(/[\\/:*?"<>|]/g, "");
    const nombreArchivoResultados = `${nombrePacienteUpper}_${codigoSolicitudClean}.pdf`;

    doc.setProperties({
      title: `${nombrePacienteUpper}_${codigoSolicitudClean}`,
    });

    const tieneEmpresa = solicitudInfo.nombreConvenio && solicitudInfo.nombreConvenio !== "-" && String(solicitudInfo.nombreConvenio).trim() !== "";
    const boxHeight = tieneEmpresa ? 20 : 16.5;
    const topTableMargin = tieneEmpresa ? 59 : 55;
    const yMuestra = 35 + boxHeight + 3.5;

    // Cargar resultados y construir árboles en paralelo
    let rawResultados = [];
    if (resRes && resRes.estatus === "OK" && Array.isArray(resRes.resultados)) {
      rawResultados = resRes.resultados;
    }

    const resultMap = {};
    rawResultados.forEach(r => {
      const key = `${r.idEstudio}-${r.idParametrosEstudio || r.idParametro}`;
      resultMap[key] = r;
    });

    let estudiosListaTarget = studiesInput;
    if (!estudiosListaTarget || estudiosListaTarget.length === 0) {
      estudiosListaTarget = Array.isArray(resSolEst) ? resSolEst : (resSolEst?.estudios || []);
    }

    const paramItemsMapByEstudio = {};

    const cargarNodoRecursivo = async (estNodo, idPadre = null) => {
      const idEstNode = estNodo.idEstudio || estNodo.id;
      let detEst = estNodo;

      const needsDet = idEstNode && (!estNodo.tipoMuestra || !estNodo.metodologia);
      const needsMuestras = idEstNode && !detEst.tipoMuestra;
      const needsParams = idEstNode && !paramItemsMapByEstudio[idEstNode];

      const [fetched, resMuestras, pList, rawInt] = await Promise.all([
        needsDet ? getEstudioDetalleConCache(idEstNode) : Promise.resolve(null),
        needsMuestras ? getMuestrasConCache(idEstNode) : Promise.resolve([]),
        needsParams ? ParametrosEstudioService.getParametrosPorEstudio(idEstNode).catch(() => []) : Promise.resolve(null),
        idEstNode ? EstudiosIntService.getEstudiosPorEstudio(idEstNode).catch(() => []) : Promise.resolve([])
      ]);

      if (fetched) detEst = { ...estNodo, ...fetched };

      let tipoM = detEst.tipoMuestra || detEst.muestra || detEst.material || "";
      if (!tipoM && Array.isArray(resMuestras) && resMuestras.length > 0) {
        tipoM = resMuestras[0].tipoMuestra || resMuestras[0].nombreMaterial || resMuestras[0].material || tipoM;
      }
      detEst.tipoMuestra = tipoM;

      if (pList && !paramItemsMapByEstudio[idEstNode]) {
        paramItemsMapByEstudio[idEstNode] = pList.map(p => {
          const pId = p.idParametrosEstudio || p.id;
          const rObj = resultMap[`${idEstNode}-${pId}`] || {};
          return {
            type: "parameter",
            idEstudio: idEstNode,
            parametro: p,
            resultValue: rObj.valor || "",
            verificado: rObj.verificado || false,
            idResultado: rObj.idResultado
          };
        });
      }

      const hijosNodos = [];
      if (Array.isArray(rawInt) && rawInt.length > 0) {
        const subHijos = await Promise.all(
          rawInt.map(async (sub) => {
            const idSec = sub.idEstudioSec || sub.idEstudio || sub.id_estudio_sec;
            if (!idSec) return null;
            const subDet = await getEstudioDetalleConCache(idSec);
            if (!subDet) return null;
            return cargarNodoRecursivo(subDet, idEstNode);
          })
        );
        hijosNodos.push(...subHijos.filter(Boolean));
      }

      return {
        estudio: detEst,
        idPadre: idPadre,
        hijos: hijosNodos,
      };
    };

    const tieneValorParametro = (item) => {
      if (!item) return false;
      const raw = item.resultValue !== undefined ? item.resultValue : (item.valor !== undefined ? item.valor : "");
      if (raw === null || raw === undefined) return false;
      const clean = htmlToPlainText(String(raw)).trim();
      return clean.length > 0;
    };

    const subArbolTieneValoresMismaTabla = (n) => {
      if (!n) return false;
      const cEst = n.estudio || {};
      if (isSubEstudioFlag(cEst)) return false;

      const cId = cEst.idEstudio || cEst.id;
      const cParams = paramItemsMapByEstudio[cId] || [];
      if (cParams.some(p => tieneValorParametro(p))) {
        return true;
      }

      for (const nieto of n.hijos || []) {
        if (!isSubEstudioFlag(nieto.estudio)) {
          if (subArbolTieneValoresMismaTabla(nieto)) {
            return true;
          }
        }
      }
      return false;
    };

    const generarTablasPDF = (node, rootIdTarget = null) => {
      const tablas = [];
      const est = node.estudio || {};
      const idE = est.idEstudio || est.id;
      const currentRootId = rootIdTarget || idE;
      const params = paramItemsMapByEstudio[idE] || [];
      const hijos = node.hijos || [];
      const esSub = isSubEstudioFlag(est);
      const tieneParamsMain = params.length > 0;
      const tieneHijos = hijos.length > 0;

      if (esSub) {
        if (tieneParamsMain) {
          if (params.some(p => tieneValorParametro(p))) {
            tablas.push({
              nombreTitulo: (est.nombre || "").toUpperCase(),
              estudioPrincipalTabla: est,
              rootIdTarget: currentRootId,
              rows: params.map(p => ({ type: "parameter", item: p })),
            });
          }
          for (const child of hijos) {
            tablas.push(...generarTablasPDF(child, currentRootId));
          }
        } else {
          if (tieneHijos) {
            let headerSuperiorAsignado = false;
            for (let i = 0; i < hijos.length; i++) {
              const tablasHijo = generarTablasPDF(hijos[i], currentRootId);
              if (tablasHijo.length > 0) {
                if (!headerSuperiorAsignado) {
                  tablasHijo[0].nombreHeaderSuperior = (est.nombre || "").toUpperCase();
                  headerSuperiorAsignado = true;
                }
                tablas.push(...tablasHijo);
              }
            }
          }
        }
      } else {
        const rowsBox = [];
        if (tieneParamsMain) {
          params.forEach(p => rowsBox.push({ type: "parameter", item: p }));
        }

        const agregarSubEstudiosMismaTabla = (n) => {
          for (const child of n.hijos || []) {
            const childEst = child.estudio || {};
            const esSubFlag = isSubEstudioFlag(childEst);
            if (esSubFlag) continue;

            if (!subArbolTieneValoresMismaTabla(child)) {
              continue;
            }

            const childIdE = childEst.idEstudio || childEst.id;
            const childParams = paramItemsMapByEstudio[childIdE] || [];
            const esMismoNombreUnicoParam =
              !esSubFlag &&
              childParams.length === 1 &&
              normStr(childEst.nombre) === normStr(childParams[0]?.parametro?.parametro || childParams[0]?.parametro?.nombre);

            if (!esMismoNombreUnicoParam) {
              rowsBox.push({
                type: "subHeader",
                nombre: (childEst.nombre || "").toUpperCase(),
              });
            }

            childParams.forEach(p => rowsBox.push({ type: "parameter", item: p }));

            if ((child.hijos || []).length > 0) {
              if (!isSubEstudioFlag(childEst)) {
                agregarSubEstudiosMismaTabla(child);
              }
            }
          }
        };

        agregarSubEstudiosMismaTabla(node);

        if (rowsBox.some(r => r.type === "parameter" && tieneValorParametro(r.item))) {
          tablas.push({
            nombreTitulo: (est.nombre || "").toUpperCase(),
            estudioPrincipalTabla: est,
            rootIdTarget: currentRootId,
            rows: rowsBox,
          });
        }

        const agregarSubTablasAparte = (n) => {
          for (const child of n.hijos || []) {
            if (isSubEstudioFlag(child.estudio)) {
              tablas.push(...generarTablasPDF(child, currentRootId));
            } else if ((child.hijos || []).length > 0) {
              agregarSubTablasAparte(child);
            }
          }
        };
        agregarSubTablasAparte(node);
      }

      return tablas;
    };

    const tablasPDFList = [];

    const tablasPorEstudio = await Promise.all(
      estudiosListaTarget.map(async (stTarget) => {
        const idEstTarget = stTarget.idEstudioPadre || stTarget.idEstudio || stTarget.id;
        if (!idEstTarget) return [];
        const detMain = (await getEstudioDetalleConCache(idEstTarget)) || stTarget;
        const arbolMain = await cargarNodoRecursivo(detMain, null);
        return generarTablasPDF(arbolMain, idEstTarget);
      })
    );

    for (const tList of tablasPorEstudio) {
      if (Array.isArray(tList)) {
        tablasPDFList.push(...tList);
      }
    }

    const tablasValidas = tablasPDFList.filter(t => {
      return (t.rows || []).some(
        r => r.type === "parameter" && tieneValorParametro(r.item)
      );
    });

    if (tablasValidas.length === 0) {
      await Swal.fire({
        icon: "info",
        title: "Sin resultados para imprimir",
        text: "Los estudios de esta solicitud no cuentan con resultados capturados para imprimir.",
        confirmButtonColor: "#006787"
      });
      return { blob: null, doc: null, cancelled: true };
    }

    let currentY = yMuestra;

    for (let tIdx = 0; tIdx < tablasValidas.length; tIdx++) {
      const tablaData = tablasValidas[tIdx];

      const tieneParamBR = (tablaData.rows || []).some(
        r => r.type === "parameter" && esParametroBR(r.item?.parametro)
      );

      const tableHeadRow2 = tieneParamBR
        ? [
          { content: "", styles: { fillColor: [210, 235, 250], cellPadding: 1 } },
          { content: "Bajo", styles: { fillColor: [210, 235, 250], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 9.0, halign: "center", cellPadding: 1 } },
          { content: "Dentro", styles: { fillColor: [210, 235, 250], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 9.0, halign: "center", cellPadding: 1 } },
          { content: "Sobre", styles: { fillColor: [210, 235, 250], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 9.0, halign: "center", cellPadding: 1 } },
          { content: "UM", styles: { fillColor: [210, 235, 250], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 9.0, halign: "center", cellPadding: 1 } },
          { content: "Intervalo Biológico de Referencia", styles: { fillColor: [210, 235, 250], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 9.0, halign: "center", cellPadding: 1 } }
        ]
        : [
          { content: "", styles: { fillColor: [210, 235, 250], cellPadding: 1 } },
          { content: "Resultado", colSpan: 3, styles: { fillColor: [210, 235, 250], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 9.0, halign: "center", cellPadding: 1 } },
          { content: "UM", styles: { fillColor: [210, 235, 250], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 9.0, halign: "center", cellPadding: 1 } },
          { content: "Intervalo Biológico de Referencia", styles: { fillColor: [210, 235, 250], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 9.0, halign: "center", cellPadding: 1 } }
        ];

      if (currentY > 195) {
        doc.addPage();
        currentY = topTableMargin + 3.5;
      }

      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text("Fecha de toma de muestra primaria:", 10, currentY);
      const labelMuestraWidth = doc.getTextWidth("Fecha de toma de muestra primaria: ");
      doc.setFont("helvetica", "normal");
      doc.text(solicitudInfo.fechaSolicitudFormateada || "-", 10 + labelMuestraWidth, currentY);
      currentY += 2.8;

      const headRows = [];
      if (tablaData.nombreHeaderSuperior) {
        headRows.push([
          {
            content: tablaData.nombreHeaderSuperior,
            colSpan: 6,
            styles: {
              fillColor: [210, 235, 250],
              textColor: [0, 0, 0],
              fontStyle: "bolditalic",
              fontSize: 12.5,
              halign: "left",
              cellPadding: 1.8
            }
          }
        ]);
      }

      headRows.push([
        {
          content: (tablaData.nombreTitulo || "").toUpperCase(),
          colSpan: 6,
          styles: {
            fillColor: [210, 235, 250],
            textColor: [0, 0, 0],
            fontStyle: "bolditalic",
            fontSize: 12.5,
            halign: "left",
            cellPadding: 1.8
          }
        }
      ]);
      headRows.push(tableHeadRow2);

      const tableBodyRows = [];

      (tablaData.rows || []).forEach(rObj => {
        if (rObj.type === "subHeader") {
          tableBodyRows.push([
            {
              content: String(rObj.nombre || "").toUpperCase(),
              colSpan: 6,
              isSubHeader: true,
              styles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: "bolditalic",
                fontSize: 9.0,
                halign: "left",
                cellPadding: 1.4
              }
            }
          ]);
        } else if (rObj.type === "parameter") {
          const item = rObj.item || {};
          const p = item.parametro || {};
          const paramName = (p.parametro || p.nombre || "").toUpperCase();
          const resultVal = item.resultValue || "";
          const cleanResult = htmlToPlainText(resultVal);
          const unidad = p.unidadMedida || p.unidad || "";
          const pacCompat = {
            sexo: solicitudInfo.sexo,
            edad: solicitudInfo.edad
          };
          const refStr = formatParametroIntervalos(p, pacCompat);

          let bajoCell = "";
          let dentroCell = "";
          let sobreCell = "";

          const numVal = parseFloat(cleanResult);
          const isVerified = item.verificado === true || item.verificado === 1 || String(item.verificado) === "true";
          const valDisplay = isVerified ? `${cleanResult}\n(Verificado)` : cleanResult;

          const esBR = esParametroBR(p);

          if (esBR && tieneParamBR) {
            if (!isNaN(numVal) && refStr) {
              let min = null;
              let max = null;

              const rangeMatch = String(refStr).match(/(\d+(?:\.\d+)?)\s*[-–—aA]\s*(\d+(?:\.\d+)?)/);
              if (rangeMatch) {
                min = parseFloat(rangeMatch[1]);
                max = parseFloat(rangeMatch[2]);
              } else {
                const maxMatch = String(refStr).match(/<=\s*(\d+(?:\.\d+)?)|<\s*(\d+(?:\.\d+)?)/);
                if (maxMatch) {
                  min = -Infinity;
                  max = parseFloat(maxMatch[1] || maxMatch[2]);
                } else {
                  const minMatch = String(refStr).match(/>=\s*(\d+(?:\.\d+)?)|>\s*(\d+(?:\.\d+)?)/);
                  if (minMatch) {
                    min = parseFloat(minMatch[1] || minMatch[2]);
                    max = Infinity;
                  }
                }
              }

              if (min !== null && max !== null) {
                if (numVal < min) {
                  bajoCell = valDisplay;
                } else if (numVal > max) {
                  sobreCell = valDisplay;
                } else {
                  dentroCell = valDisplay;
                }
              } else {
                dentroCell = valDisplay;
              }
            } else {
              dentroCell = valDisplay;
            }

            tableBodyRows.push([
              paramName,
              bajoCell,
              dentroCell,
              sobreCell,
              unidad,
              refStr
            ]);
          } else {
            tableBodyRows.push([
              paramName,
              { content: valDisplay, colSpan: 3, styles: { halign: "center" } },
              unidad,
              refStr
            ]);
          }

          if (p.textoExtra && htmlToPlainText(p.textoExtra).trim() !== "") {
            tableBodyRows.push([
              {
                content: htmlToPlainText(p.textoExtra),
                colSpan: 6,
                styles: { halign: "left", fontStyle: "normal", fontSize: 8.5, cellPadding: 1.2 }
              }
            ]);
          }
        }
      });

      autoTable(doc, {
        startY: currentY,
        head: headRows,
        body: tableBodyRows,
        theme: "plain",
        showHead: "firstPage",
        headStyles: {
          lineWidth: 0,
        },
        bodyStyles: {
          fontSize: 9.0,
          textColor: [0, 0, 0],
          valign: "middle",
          cellPadding: { top: 0.9, bottom: 0.9, left: 1, right: 1 },
          lineWidth: 0,
        },
        columnStyles: {
          0: { cellWidth: 58, halign: "left" },
          1: { cellWidth: 16, halign: "center" },
          2: { cellWidth: 16, halign: "center" },
          3: { cellWidth: 16, halign: "center" },
          4: { cellWidth: 16, halign: "center" },
          5: { cellWidth: 68, halign: "center" }
        },
        didParseCell: function (data) {
          if (data.section === "body") {
            if ([1, 3].includes(data.column.index) && data.cell.raw && String(data.cell.raw).trim() !== "") {
              data.cell.styles.fontStyle = "bold";
            }
            if (data.column.index === 5) {
              data.cell.styles.fontStyle = "bold";
            }
            if ([1, 2, 3].includes(data.column.index)) {
              const rawVal = data.cell.raw?.content !== undefined ? data.cell.raw.content : data.cell.raw;
              const cellText = Array.isArray(data.cell.text) ? data.cell.text.join("\n") : String(rawVal || "");
              if (cellText.includes("(Verificado)")) {
                data.cell.styles.fontStyle = "bold";
                data.cell.styles.textColor = [0, 0, 0];
                data.cell.styles.fontSize = 7.2;
              }
            }
          }
        },
        didDrawCell: function (data) {
          if (tieneParamBR) {
            const isHeaderRow2 = data.section === "head" && data.row.index === (headRows.length - 1);
            const isBodyRow = data.section === "body" && !data.row.raw?.[0]?.isSubHeader;

            if (isHeaderRow2) {
              data.doc.saveGraphicsState();
              data.doc.setDrawColor(0, 0, 0);
              data.doc.setLineWidth(0.25);
              // Líneas verticales delimitando Bajo, Dentro, Sobre y UM
              if (data.column.index === 2) {
                data.doc.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height);
                data.doc.line(data.cell.x + data.cell.width, data.cell.y, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
              } else if (data.column.index === 3) {
                data.doc.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height);
                data.doc.line(data.cell.x + data.cell.width, data.cell.y, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
              } else if (data.column.index === 4) {
                data.doc.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height);
              }
              // Sin línea horizontal en el encabezado (Punto 4: esas no van)
              data.doc.restoreGraphicsState();
            } else if (isBodyRow) {
              // En las filas de datos: líneas verticales delimitando la columna Dentro
              if (data.column.index === 2) {
                data.doc.saveGraphicsState();
                data.doc.setDrawColor(0, 0, 0);
                data.doc.setLineWidth(0.25);
                data.doc.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height);
                data.doc.line(data.cell.x + data.cell.width, data.cell.y, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
                data.doc.restoreGraphicsState();
              } else if (data.column.index === 3) {
                data.doc.saveGraphicsState();
                data.doc.setDrawColor(0, 0, 0);
                data.doc.setLineWidth(0.25);
                data.doc.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height);
                data.doc.restoreGraphicsState();
              }
            }
          }
        },
        margin: { top: topTableMargin, bottom: 88, left: 10, right: 10 }
      });

      let finalY = doc.lastAutoTable.finalY + 3.5;
      const estPrincipalTabla = tablaData.estudioPrincipalTabla || {};
      const idEstTabla = estPrincipalTabla.idEstudio || estPrincipalTabla.id;

      // Renderizar Observación / NOTA del estudio debajo de la tabla
      // Si no son subexamenes (vienen juntos en una sola tabla), solo se toma la observación del padre.
      // Si sí son subexamenes (tablas separadas), se toma la observación de ese subexamen.
      const idsEstaTabla = new Set([String(idEstTabla)]);
      (tablaData.rows || []).forEach(r => {
        const rId = r.item?.idEstudio || r.idEstudio;
        if (rId) idsEstaTabla.add(String(rId));
      });

      let obsRaw = "";
      if (Array.isArray(rawResultados)) {
        const resPadre = rawResultados.find(
          r => String(r.idEstudio) === String(idEstTabla) && r.observaciones && String(r.observaciones).trim() !== ""
        );
        if (resPadre) {
          obsRaw = resPadre.observaciones;
        } else {
          const resConObs = rawResultados.find(
            r => idsEstaTabla.has(String(r.idEstudio)) && r.observaciones && String(r.observaciones).trim() !== ""
          );
          if (resConObs) {
            obsRaw = resConObs.observaciones;
          }
        }
      }
      if (!obsRaw && (tablaData.rows || []).length > 0) {
        for (const rItem of tablaData.rows) {
          const rowEstId = String(rItem.item?.idEstudio || idEstTabla);
          if (!idsEstaTabla.has(rowEstId)) continue;
          const pId = rItem.item?.parametro?.idParametrosEstudio || rItem.item?.parametro?.id;
          const rObj = resultMap[`${rowEstId}-${pId}`] || resultMap[`${idEstTabla}-${pId}`];
          if (rObj && rObj.observaciones && String(rObj.observaciones).trim()) {
            obsRaw = rObj.observaciones;
            break;
          }
        }
      }

      const obsTexto = htmlToPlainText(obsRaw).trim();

      let metVal = String(estPrincipalTabla.metodologia || estPrincipalTabla.metodo || estPrincipalTabla.tecnica || "").trim();
      let muestraVal = String(estPrincipalTabla.tipoMuestra || estPrincipalTabla.muestra || estPrincipalTabla.material || "").trim();

      if ((!metVal || !muestraVal) && idEstTabla) {
        const [det, resM] = await Promise.all([
          !metVal ? getEstudioDetalleConCache(idEstTabla) : Promise.resolve(null),
          !muestraVal ? getMuestrasConCache(idEstTabla) : Promise.resolve([])
        ]);
        if (!metVal && det) {
          metVal = String(det?.metodologia || det?.metodo || det?.tecnica || "").trim();
        }
        if (!muestraVal && Array.isArray(resM) && resM.length > 0) {
          muestraVal = String(resM[0].tipoMuestra || resM[0].nombreMaterial || resM[0].material || "").trim();
        }
      }

      let regLiberadoTabla = null;
      if (idSol) {
        const rawIds = [
          idEstTabla,
          estPrincipalTabla?.idEstudioPadre,
          estPrincipalTabla?.idPadre,
          estPrincipalTabla?.idEstudioSec,
          tablaData.rootIdTarget,
        ].filter(Boolean);

        const idsToTry = [...new Set(rawIds)];

        const histResults = await Promise.all(
          idsToTry.map(idCheck => getHistorialEstudioConCache(idSol, idCheck))
        );
        for (const histEst of histResults) {
          if (Array.isArray(histEst)) {
            const found = histEst.find(h => String(h.tipoAccion).toUpperCase() === "L" || String(h.tipoAccion).toUpperCase() === "LIBERADO");
            if (found) {
              regLiberadoTabla = found;
              break;
            }
          }
        }

        if (!regLiberadoTabla) {
          try {
            const localHist = HistorialEstudiosService._getLocalHistorial();
            const regLocal = localHist.find(
              h => String(h.idSolicitud) === String(idSol) &&
                idsToTry.map(String).includes(String(h.idEstudio)) &&
                (String(h.tipoAccion).toUpperCase() === "L" || String(h.tipoAccion).toUpperCase() === "LIBERADO")
            );
            if (regLocal) {
              regLiberadoTabla = regLocal;
            }
          } catch (eLoc) { }
        }
      }

      let nomLiberadorTabla = regLiberadoTabla?.nombreEmpleado || currentU?.nombre || "EMPLEADO LIBERADOR";
      let cedTabla = currentU?.cedulaProf || currentU?.cedula || "";
      let fechaLibTabla = fechaImpresionStr;

      if (regLiberadoTabla && (regLiberadoTabla.fechaModificacion || regLiberadoTabla.fecha)) {
        const rawF = regLiberadoTabla.fechaModificacion || regLiberadoTabla.fecha;
        fechaLibTabla = formatFechaHoraEspanol(rawF);
      }

      if (finalY > 188) {
        doc.addPage();
        finalY = topTableMargin + 6;
      }

      const rightX = 200;
      const line1Segments = [
        { text: "Metodología: ", bold: true },
        { text: (metVal || "-") + "   ", bold: false },
        { text: "Tipo de Muestra: ", bold: true },
        { text: (muestraVal || "-"), bold: false }
      ];
      drawRichTextRight(doc, line1Segments, rightX, finalY, 7.5);

      finalY += 3.8;
      const line2Segments = [
        { text: "Liberó: ", bold: true },
        { text: (nomLiberadorTabla || "-") + "   ", bold: false },
        { text: "Ced.Prof.: ", bold: true },
        { text: (cedTabla || "") + "   ", bold: false },
        { text: "Fecha y Hora de Liberación: ", bold: true },
        { text: fechaLibTabla, bold: false }
      ];
      drawRichTextRight(doc, line2Segments, rightX, finalY, 7.5);

      // Renderizar Observación / NOTA del estudio al final (debajo de Metodología, Tipo de Muestra y Liberación)
      // Renderizar Observación / NOTA del estudio al final con texto enriquecido (debajo de Metodología, Tipo de Muestra y Liberación)
      const obsBlocks = parseHtmlToStyledBlocks(obsRaw);
      if (obsBlocks.length > 0) {
        finalY += 4.5;
        finalY = renderRichTextInPdf(
          doc,
          obsBlocks,
          10,
          finalY,
          190,
          185,
          topTableMargin,
          9.0,
          4.5
        );
      }

      currentY = finalY + 6;
    }

    // Decoración de todas las páginas (Encabezado, Recuadro Paciente, Marca de Agua, QR, Firma y Pie de Página)
    // Esperar imágenes y QR precargados (cargados en paralelo durante los cálculos previos)
    const [qrUrlInforme, [headerDataUrl, logoDataUrl, firmaDataUrl, pieDataUrl]] = await Promise.all([
      qrPromise,
      imagesPromise
    ]);

    const totalPages = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      // Marca de Agua (Punto 6: más ancha para evitar que se vea alargada, uniforme 95x85mm centrada)
      try {
        doc.saveGraphicsState();
        doc.setGState(new doc.GState({ opacity: 0.11 }));
        doc.addImage(logoDataUrl || logoMembreteImg, "JPEG", 57.5, 95, 95, 85);
        doc.restoreGraphicsState();
      } catch (e) { }

      // Encabezado (ajustado a 25.5mm para igualar el ejemplo)
      try {
        doc.addImage(headerDataUrl || headerInformeImg, "PNG", 10, 7.5, 190, 25.5);
      } catch (eImg) { }

      // Recuadro de datos de paciente y solicitud en todas las páginas
      drawPatientBox(doc, solicitudInfo, 35, boxHeight);

      // Código QR de consulta (Puntos 3 y 5: más abajo y proporcionado a 26x26mm)
      if (qrUrlInforme) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(0, 0, 0);
        doc.text("Valida tus resultados aquí", 28, 218, { align: "center" });
        doc.addImage(qrUrlInforme, "PNG", 15, 220, 26, 26);
      }

      // Firma Q.F.B. Ricardo (Puntos 3 y 5: más abajo y proporcionada a 36x23mm)
      try {
        doc.addImage(firmaDataUrl || firmaRicardoImg, "JPEG", 148, 217, 36, 23);
      } catch (eSig) { }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.0);
      doc.setTextColor(0, 0, 0);
      doc.text("Q.F.B. Ricardo García Hernández", 166, 243, { align: "center" });

      // Imagen de Pie de Página (27mm)
      try {
        doc.addImage(pieDataUrl || pieDePaginaImg, "JPEG", 10, 248, 190, 27);
      } catch (eFoot) { }

      // Número de página
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`Pág. ${i}/${totalPages}`, 10, 283);
    }

    const empNombre = [currentU?.nombre, currentU?.apPaterno, currentU?.apMaterno]
      .filter(Boolean).join(" ") || currentU?.nombre || "EMPLEADO";

    const itemsMov = (studiesInput.length > 0 ? studiesInput : estudiosListaTarget).map(st => ({
      idSolicitud: idSol,
      idEstudio: st.idEstudioPadre || st.idEstudio || 0,
      nombreEstudio: st.nombre || st.nombreEstudio || "ESTUDIO",
      nombreEmpleado: empNombre,
      tipoAccion: isDownload ? "D" : "I"
    }));

    // Tareas en segundo plano (auditoría, sincronización WhatsApp y actualización a "Entregado")
    // Se ejecutan de forma asíncrona para que la descarga o apertura del visor sea inmediata
    const backgroundTasks = async () => {
      try {
        await HistorialEstudiosService.registrarMovimientoMasivo(itemsMov);
      } catch (e) { }

      try {
        const listaNombres = itemsMov.map(m => m.nombreEstudio).join(", ");
        await HistorialService.registrarEvento({
          idSolicitud: idSol,
          nombreEmpleado: empNombre,
          operacion: `${isDownload ? "Descarga" : "Impresión"} de resultados: ${listaNombres}`,
        });
      } catch (eH) { }

      if (idSol) {
        try {
          const blobSync = doc.output("blob");
          const formDataSync = new FormData();
          formDataSync.append("ticketPdf", blobSync, `informe_${idSol}.pdf`);
          formDataSync.append("idSolicitud", idSol);
          formDataSync.append("tipoDocumento", "informe");
          SolicitudesService.uploadTicketWhatsApp(formDataSync, idSol).catch((errSync) => {
            console.warn("Sincronización silenciosa de informe PDF:", errSync);
          });
        } catch (eSync) {
          console.warn("Error al generar blob para sincronización:", eSync);
        }
      }

      if (esPagado && idSol) {
        try {
          const totalStudiesRes = resSolEst || await SolicitudesEstudiosService.getEstudiosPorSolicitud(idSol);
          const totalEstudiosList = Array.isArray(totalStudiesRes)
            ? totalStudiesRes
            : totalStudiesRes?.estudios || totalStudiesRes?.data || [];
          const printHist = await HistorialEstudiosService.getHistorialSolicitudImpresionEnvio(idSol);

          const getEstudioID = (st) => String(st.idEstudio || st.id_estudio || st.idEstudioPadre || st.id || st.estudio_id || 0);

          const printedEstudioIds = new Set((printHist || []).map(h => String(h.idEstudio || h.id_estudio || h.id)));

          const esImpresionTodaSolicitud = !studiesInput || studiesInput.length === 0 || studiesInput.length >= totalEstudiosList.length;

          const estudiosImpresosAhora = (studiesInput && studiesInput.length > 0) ? studiesInput : totalEstudiosList;
          estudiosImpresosAhora.forEach(st => {
            const stId = getEstudioID(st);
            if (stId !== "0") printedEstudioIds.add(stId);
          });

          const todosEstudiosImpresos = totalEstudiosList.length > 0 && totalEstudiosList.every(st => {
            const stId = getEstudioID(st);
            return stId !== "0" && printedEstudioIds.has(stId);
          });

          if (esImpresionTodaSolicitud || todosEstudiosImpresos) {
            try {
              await SolicitudesService.updateEstatusSolicitud(idSol, "EN");
            } catch (eUpd) {
              console.warn("Fallback updateSolicitud tradicional:", eUpd);
              try {
                await SolicitudesService.updateSolicitud({
                  solicitud: { ...solDetalle, estatusResultados: "EN", estatus: "P" },
                  estudios: totalEstudiosList
                }, idSol);
              } catch (eFallback) { }
            }
            await HistorialService.registrarEvento({
              idSolicitud: idSol,
              nombreEmpleado: currentU?.nombre || "EMPLEADO",
              operacion: "Solicitud marcada como Entregada (EN) al imprimirse/descargarse todos sus estudios"
            });
          }
        } catch (eDelivery) {
          console.error("Error al actualizar estatus a entregado en segundo plano:", eDelivery);
        }
      }
    };
    backgroundTasks();

    if (returnBlobOnly) {
      return { blob: doc.output("blob"), doc, cancelled: false };
    }

    if (isDownload) {
      doc.save(nombreArchivoResultados);
      mostrarToast("Informe de resultados descargado", "success");
    } else {
      const pdfBlob = doc.output("bloburl");
      Swal.fire({
        title: "Informe de Resultados",
        html: `
          <div style="width: 100%; height: 75vh;">
            <iframe src="${pdfBlob}#toolbar=1" width="100%" height="100%" style="border: none; border-radius: 8px;"></iframe>
          </div>
        `,
        width: "82vw",
        showConfirmButton: false,
        showCloseButton: true
      });
    }

    return { doc, cancelled: false };
  } catch (error) {
    console.error("Error al generar PDF de resultados:", error);
    Swal.fire("Error", "No se pudo generar el informe de resultados.", "error");
    return null;
  }
};
